import { useCallback, useEffect, useState } from "react";
import type {
  ComorbidityAssessment,
  ComorbidityConditionAssessment,
  ComorbidityEvidenceSource,
  ComorbidityVerificationStatus,
} from "@care-by-ella/contracts";
import {
  COMORBIDITY_EVIDENCE_SOURCES,
  COMORBIDITY_VERIFICATION_STATUSES,
} from "@care-by-ella/contracts";
import {
  fetchComorbidityAssessment,
  putComorbidityVerification,
} from "@/lib/comorbidity-api";
import { useReadOnly } from "./SupportView";

// TES-564 — the BMI 27–30 assessment block (the "Läkaradmin" document).
// One component, mounted on the patient page and in the meeting
// view: BMI with its provenance, then one row per condition the patient
// reported in F-02 — her own status ("aktuell/tidigare/osäker"), whether the
// condition is on the qualifying core list, the doctor's verification and
// its evidence source, and who verified it when. Raw enums never render;
// every value has a Swedish label. The block informs the doctor's decision,
// it never makes it: nothing here approves or blocks a treatment.

const CONDITION_LABELS: Record<string, string> = {
  typ2_diabetes: "Typ 2-diabetes",
  prediabetes: "Prediabetes",
  hogt_blodtryck: "Högt blodtryck",
  blodfetter: "Förhöjda blodfetter",
  hjart_karl: "Hjärt-kärlsjukdom",
  stroke_tia: "Stroke/TIA",
  somnapne: "Sömnapné",
  pcos: "PMOS",
  fettlever: "Fettlever",
  ledsjukdom: "Ledsjukdom",
  gikt: "Gikt",
  annat: "Annat",
};

const PATIENT_STATUS_LABELS: Record<string, string> = {
  aktuellt: "Aktuellt",
  tidigare: "Tidigare",
  osaker: "Osäker",
};

const VERIFICATION_LABELS: Record<ComorbidityVerificationStatus, string> = {
  EJ_GRANSKAD: "Ej granskad",
  VERIFIERAD: "Verifierad",
  OKLAR: "Oklar",
};

const EVIDENCE_LABELS: Record<ComorbidityEvidenceSource, string> = {
  PATIENTUPPGIFT: "Patientuppgift",
  LAKEMEDELSLISTA: "Läkemedelslista",
  BLODTRYCK: "Blodtryck",
  PROVSVAR: "Provsvar",
  JOURNAL_VARDKALLA: "Journal/vårdkälla",
  ANNAT: "Annat",
};

// TES-1249 part 2 — the weight is her current reading in the measurement
// series, labelled by where it came from (CoachMeasurementSource). The
// "weight_log:*" keys are kept for an api that predates the switch.
const WEIGHT_SOURCE_LABELS: Record<string, string> = {
  "measurement:patient": "registrerad av patienten",
  "measurement:weekly_checkin": "från veckans check-in",
  "measurement:f01": "från F-01",
  "measurement:form": "från ett formulär",
  "measurement:clinician": "registrerad av vårdteamet",
  "measurement:import": "importerad",
  "weight_log:patient": "loggad av patienten",
  "weight_log:doctor": "registrerad av läkare",
  "weight_log:webdoc": "från Webdoc",
  "weight_log:checkin": "från veckans check-in",
  "weight_log:f01": "från F-01",
  intake: "från hälsodeklarationen",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("sv-SE", { dateStyle: "short" });
}

function num(n: number | null, unit: string): string {
  return n == null ? "—" : `${String(n).replace(".", ",")} ${unit}`.trim();
}

function statusTone(status: ComorbidityVerificationStatus): string {
  switch (status) {
    case "VERIFIERAD":
      return "bg-brand/10 text-brand";
    case "OKLAR":
      return "bg-warning/15 text-warning";
    default:
      return "bg-surfaceMuted text-textSecondary";
  }
}

function ConditionRow({
  row,
  saving,
  onSave,
}: {
  row: ComorbidityConditionAssessment;
  saving: boolean;
  onSave: (
    status: ComorbidityVerificationStatus,
    evidenceSource: ComorbidityEvidenceSource | null,
  ) => void;
}) {
  // TES-1040 — the admin's read-only view shows the stored verification
  // (the pill and the line below), not the controls that change it.
  const readOnly = useReadOnly();
  const [status, setStatus] = useState<ComorbidityVerificationStatus>(
    row.verification.status,
  );
  const [evidence, setEvidence] = useState<ComorbidityEvidenceSource | "">(
    row.verification.evidenceSource ?? "",
  );
  // A server re-derive (after any row's save) resets the draft to what is
  // stored, so two rows never show one row's stale draft.
  useEffect(() => {
    setStatus(row.verification.status);
    setEvidence(row.verification.evidenceSource ?? "");
  }, [row.verification.status, row.verification.evidenceSource]);

  const needsEvidence = status !== "EJ_GRANSKAD";
  const dirty =
    status !== row.verification.status ||
    (evidence || null) !== row.verification.evidenceSource;
  const valid = !needsEvidence || evidence !== "";
  const selectClass =
    "rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm";

  return (
    <li className="flex flex-col gap-xs border-t border-surfaceMuted pt-sm first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-sm">
        <span className="font-semibold">
          {CONDITION_LABELS[row.conditionKey] ?? row.conditionKey}
        </span>
        {row.coreList ? (
          <span className="rounded-full bg-brand/10 px-sm py-[2px] text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">
            Kärnlista
          </span>
        ) : null}
        <span className="text-sm text-textSecondary">
          Patienten:{" "}
          {row.patientStatus
            ? PATIENT_STATUS_LABELS[row.patientStatus]
            : "ej angivet (äldre hälsodeklaration)"}
        </span>
        <span
          className={`ml-auto rounded-full px-sm py-[2px] text-xs font-semibold ${statusTone(row.verification.status)}`}
        >
          {VERIFICATION_LABELS[row.verification.status]}
        </span>
      </div>
      {row.otherText ? (
        <p className="text-sm text-textSecondary">”{row.otherText}”</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-sm">
        {readOnly ? null : (
          <>
            <label className="flex items-center gap-xs text-sm">
              <span className="text-textSecondary">Verifiering</span>
              <select
                className={selectClass}
                value={status}
                disabled={saving}
                onChange={(e) =>
                  setStatus(e.target.value as ComorbidityVerificationStatus)
                }
              >
                {COMORBIDITY_VERIFICATION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {VERIFICATION_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-xs text-sm">
              <span className="text-textSecondary">Underlag</span>
              <select
                className={selectClass}
                value={evidence}
                disabled={saving || !needsEvidence}
                onChange={(e) =>
                  setEvidence(e.target.value as ComorbidityEvidenceSource | "")
                }
              >
                <option value="">
                  {needsEvidence ? "Välj underlag…" : "—"}
                </option>
                {COMORBIDITY_EVIDENCE_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {EVIDENCE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={!dirty || !valid || saving}
              onClick={() =>
                onSave(status, needsEvidence ? evidence || null : null)
              }
              className="rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90 disabled:opacity-40"
            >
              {saving ? "Sparar…" : "Spara"}
            </button>
          </>
        )}
        {row.verification.verifiedAt ? (
          <span className="text-xs text-textSecondary">
            {row.verification.status === "VERIFIERAD" ? "Verifierad" : "Bedömd"}{" "}
            av {row.verification.verifiedByName ?? "läkare"},{" "}
            {fmtDate(row.verification.verifiedAt)}
            {row.verification.evidenceSource
              ? ` · ${EVIDENCE_LABELS[row.verification.evidenceSource]}`
              : ""}
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function ComorbidityAssessmentPanel({
  patientId,
  compact = false,
}: {
  patientId: string;
  /** Meeting view: no outer card chrome, the pane supplies it. */
  compact?: boolean;
}) {
  const [data, setData] = useState<ComorbidityAssessment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchComorbidityAssessment(patientId)
      .then((d) => alive && setData(d))
      .catch(
        (e: unknown) =>
          alive &&
          setError(e instanceof Error ? e.message : "Kunde inte hämta."),
      );
    return () => {
      alive = false;
    };
  }, [patientId]);

  const save = useCallback(
    async (
      row: ComorbidityConditionAssessment,
      status: ComorbidityVerificationStatus,
      evidenceSource: ComorbidityEvidenceSource | null,
    ) => {
      setSavingKey(row.conditionKey);
      setError(null);
      try {
        setData(
          await putComorbidityVerification(patientId, {
            conditionKey: row.conditionKey,
            status,
            evidenceSource,
          }),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : "Kunde inte spara.");
      } finally {
        setSavingKey(null);
      }
    },
    [patientId],
  );

  const body = (() => {
    if (error && !data) return <p className="text-sm text-danger">{error}</p>;
    if (!data) return <p className="text-sm text-textSecondary">Hämtar…</p>;
    const b = data.bmi;
    return (
      <div className="flex flex-col gap-sm">
        <p className="text-sm">
          <span className="font-display text-[22px] leading-7">
            {b.value == null
              ? "BMI —"
              : `BMI ${String(b.value).replace(".", ",")}`}
          </span>
          <span className="ml-sm text-textSecondary">
            vikt {num(b.weightKg, "kg")}
            {b.weightAt
              ? ` (${fmtDate(b.weightAt)}${b.weightSource ? `, ${WEIGHT_SOURCE_LABELS[b.weightSource] ?? b.weightSource}` : ""})`
              : ""}{" "}
            · längd {num(b.heightCm, "cm")}
            {b.heightAt
              ? ` (F-02${data.intake ? ` v${data.intake.templateVersion}` : ""}, ${fmtDate(b.heightAt)})`
              : ""}
          </span>
        </p>
        {b.inRange27to30 ? (
          <p
            className={`rounded-md px-sm py-xs text-sm ${
              data.lacksVerifiedCurrentComorbidity
                ? "bg-warning/15 text-warning"
                : "bg-brand/10 text-brand"
            }`}
          >
            {data.lacksVerifiedCurrentComorbidity
              ? "BMI 27–30 utan verifierad aktuell samsjuklighet ur kärnlistan — får inte godkännas automatiskt; läkarbedömning krävs."
              : "BMI 27–30 med verifierad aktuell samsjuklighet ur kärnlistan."}
          </p>
        ) : null}
        {!data.intake ? (
          <p className="text-sm text-textSecondary">
            Ingen hälsodeklaration inlämnad än.
          </p>
        ) : data.conditions.length === 0 ? (
          <p className="text-sm text-textSecondary">
            Patienten angav ingen viktrelaterad samsjuklighet i F-02.
          </p>
        ) : (
          <ul className="flex flex-col gap-sm">
            {data.conditions.map((row) => (
              <ConditionRow
                key={row.conditionKey}
                row={row}
                saving={savingKey === row.conditionKey}
                onSave={(status, evidence) => void save(row, status, evidence)}
              />
            ))}
          </ul>
        )}
        {error ? <p className="text-sm text-danger">{error}</p> : null}
      </div>
    );
  })();

  if (compact) return body;
  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">
        BMI 27–30 och viktrelaterad samsjuklighet
      </h2>
      <p className="mb-sm text-xs text-textSecondary">
        Patientens uppgifter ur F-02 och din verifiering. Dokumentationen ligger
        kvar i Webdoc; journalutdrag begärs bara vid medicinsk oklarhet.
      </p>
      {body}
    </section>
  );
}
